import type { DefaultOptions } from "@tanstack/react-query";

/**
 * The app's query policy, in one place so a test's QueryClient can be the same
 * client the browser runs.
 *
 * It lived inline in `main.tsx` until a 404 branch that every unit test proved
 * reachable turned out to be unreachable in production: the app throws a query
 * error to the router boundary whenever there is no cached data to fall back on,
 * and a test client built with `{ retry: false }` alone inherits none of that.
 * A component that handles an error itself must therefore say so on its own
 * query options (`throwOnError`), and the suite that proves it must start from
 * this object.
 */
export const APP_QUERY_DEFAULTS: NonNullable<DefaultOptions["queries"]> = {
	staleTime: 1000 * 60,
	// Must be >= PersistQueryClientProvider.maxAge (5 days) — TanStack docs:
	// "If lower than maxAge, garbage collection will kick in and discard the
	// stored cache earlier than expected." Otherwise any query that sits
	// idle for more than gcTime is dropped from memory, the next throttled
	// persist snapshot writes a smaller blob to IDB, and on the next
	// offline F5 those keys are missing — defeating the app-shell survival
	// promise for org context / access profile / establishments.
	gcTime: 5 * 24 * 60 * 60 * 1000,
	retry: 2,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	// 'offlineFirst' pauses retries when onlineManager says offline, so
	// queries with cached data serve stale content and queries without cached
	// data stay in a pending/loading state instead of throwing to the error
	// boundary. The reachability store (Phase 1) drives onlineManager, so the
	// transition is prompt. Workbox StaleWhileRevalidate was removed in Phase 9;
	// the IDB persister (Phase 2) + prepared datasets (Phase 7) are now the
	// sole offline cache, so we must not fire queryFn when unreachable.
	networkMode: "offlineFirst",
	// When a query errors (e.g. a flaky online request) but stale data exists,
	// don't throw to the error boundary — show the stale data instead.
	throwOnError: (_error, query) => query.state.data === undefined,
};
